import { useEffect, useState, type ReactNode } from "react";
import { useCustom, useCustomMutation, useGetLocale, useTranslate } from "@refinedev/core";
import { DateField, Button, Modal, PlusCircleSolidIcon } from "@fawkes/ui";

import { getAccessToken } from "../auth";
import { fetchAnimalPhotoBlob } from "../api/animalPhoto";
import { formatDateTime } from "../i18n/format";

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";

// WIZARDS tile migration: the SPA port of the hybrid wizard React app
// (frontend-react/wizard WizardDier.jsx `view=tegel` — Jmix AnimalWizardTileFragment/WizardDierHost).
// The old postMessage bridge (wizard:ready/data/form/submit/detail) is replaced by the api endpoints:
//   GET  /api/animals/{dierId}/wizards                       → TilePayload (wizards + runs + magUitvoeren)
//   GET  /api/animals/{dierId}/wizards/form/{definitionId}   → RunForm (frozen snapshot)
//   POST /api/animals/{dierId}/wizards/runs                  → store an execution (draft or completed)
//   GET  /api/animals/{dierId}/wizards/runs/{runId}          → RunDetail (historical values)
// ALL data labels (wizard names, field labels, summary values, status chips) come from the server
// (Dutch, authored in the wizard builder) — the client only translates its own chrome (`wizards.*`).
// The hybrid's five views map onto the tile + Modal overlays: kaarten (inline), kiezer, runner
// (widened modal with the dier sidebar), historie and detail.
//
// Start/submit affordances are gated client-side on the payload's `magUitvoeren` flag; the server
// enforces the same predicate (ROLE_ANIMAL_EDIT — the field coupling writes back to the dier) on POST.

// ── wire types (see api WizardRuntimeModels) ────────────────────────────────────────────────────────
export interface WizardStatusChip {
  label: string;
  kleur: string; // groen | oranje | rood | grijs
}

export interface WizardSummaryItem {
  label: string;
  waarde: string;
  fieldType: string;
}

export interface WizardRunSummary {
  id: string;
  definitionId: string;
  versionNumber: number | null;
  completedAt: string | null;
  completedBy: string | null;
  naam: string;
  samenvatting: WizardSummaryItem[];
  status?: WizardStatusChip | null;
}

export interface WizardCard {
  definitionId: string;
  name: string;
  category: string | null;
  versionNumber: number | null;
  tegelTitel: string;
  icon: string;
  laatsteRun?: WizardRunSummary | null;
}

export interface WizardsTilePayload {
  target: { type: string; id: number };
  dier?: {
    id: number;
    naam: string | null;
    ras: string | null;
    diersoort: string | null;
    chipnummer: string | null;
    geboortedatum: string | null;
    geslacht: string | null;
    leeftijd: string | null;
    fotoUrl: string | null;
    verblijf?: { actief: boolean; reden: string | null; nummer: string | null; sinds: string | null; dagen: number | null } | null;
  } | null;
  wizards: WizardCard[];
  runs: WizardRunSummary[];
  magUitvoeren: boolean;
}

export interface WizardFormField {
  fieldKey: string;
  label: string;
  fieldType: string;
  required: boolean;
  helpText?: string | null;
  options?: { value: string; label: string }[] | null;
  scale?: boolean;
}

export interface WizardFormStep {
  title: string;
  description?: string | null;
  fields: WizardFormField[];
}

export interface WizardRunForm {
  definitionId: string;
  versionNumber: number | null;
  name: string;
  snapshot: { name?: string; description?: string | null; steps?: WizardFormStep[] };
}

export interface WizardRunDetail {
  id: string;
  versionNumber: number | null;
  completedAt: string | null;
  completedBy: string | null;
  status?: WizardStatusChip | null;
  snapshot?: { name?: string } | null;
  values: { fieldKey: string; label: string; fieldType: string; weergave: string }[];
}

type WaardenState = Record<string, unknown>;

// Field-type icons on the summary rows — verbatim the hybrid `iconVoor`.
function iconVoor(type: string): string {
  switch (type) {
    case "DATE":
      return "🗓️";
    case "BOOLEAN":
      return "✔️";
    case "NUMBER":
      return "🔢";
    case "SELECT":
      return "◉";
    case "MULTISELECT":
      return "☑️";
    default:
      return "📝";
  }
}

function StatusChip({ status, mini }: { status: WizardStatusChip | null | undefined; mini?: boolean }) {
  if (!status) return null;
  const kleur = ["groen", "oranje", "rood", "grijs"].includes(status.kleur) ? status.kleur : "grijs";
  return (
    <span className={`fawkes-wizards__chip${mini ? " fawkes-wizards__chip--mini" : ""} fawkes-wizards__chip--${kleur}`}>
      <span className="fawkes-wizards__chipdot" />
      {status.label}
    </span>
  );
}

// ── beoordelingsschaal (smiley + kleur) — verbatim the hybrid KeuzeKnoppen helpers ─────────────────
const SCHAAL_SMILEYS = ["😄", "🙂", "😐", "😟", "😠"];
function schaalSmiley(i: number, n: number): string {
  if (n <= 1) return "🙂";
  return SCHAAL_SMILEYS[Math.round((i / (n - 1)) * (SCHAAL_SMILEYS.length - 1))];
}
function schaalKleur(i: number, n: number): string {
  const t = n <= 1 ? 0 : i / (n - 1);
  return `hsl(${Math.round(125 - t * 125)}, 52%, 42%)`;
}

function KeuzeKnoppen({ f, value, onChange, multi }: { f: WizardFormField; value: unknown; onChange: (v: unknown) => void; multi: boolean }) {
  const opties = f.options ?? [];
  const schaal = f.fieldType === "SELECT" ? f.scale !== false : f.scale === true;
  const arr = multi ? (Array.isArray(value) ? (value as string[]) : []) : null;
  const kies = (v: string) => {
    if (multi && arr) onChange(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
    else onChange(value === v ? "" : v);
  };
  return (
    <div className="fawkes-wizards__keuzes">
      {opties.map((o, i) => {
        const gekozen = multi && arr ? arr.includes(o.value) : value === o.value;
        const kleur = schaal ? schaalKleur(i, opties.length) : null;
        return (
          <button
            type="button"
            key={o.value}
            className={`fawkes-wizards__keuze${gekozen ? " fawkes-wizards__keuze--gekozen" : ""}${schaal ? " fawkes-wizards__keuze--schaal" : ""}`}
            style={gekozen && kleur ? { borderColor: kleur } : undefined}
            onClick={() => kies(o.value)}
          >
            {schaal && (
              <span className="fawkes-wizards__smiley" style={{ color: kleur ?? undefined }}>
                {schaalSmiley(i, opties.length)}
              </span>
            )}
            <span className="fawkes-wizards__keuzelabel">{o.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function VeldInput({ f, value, onChange, t }: { f: WizardFormField; value: unknown; onChange: (v: unknown) => void; t: (k: string) => string }) {
  switch (f.fieldType) {
    case "TEXTAREA": {
      const s = typeof value === "string" ? value : "";
      return (
        <div className="fawkes-wizards__textareawrap">
          <textarea className="fui-field__input" rows={3} maxLength={1000} value={s} onChange={(e) => onChange(e.target.value)} />
          <div className="fawkes-wizards__charcount">{s.length} / 1000</div>
        </div>
      );
    }
    case "NUMBER":
      return <input className="fui-field__input" type="number" value={typeof value === "string" || typeof value === "number" ? value : ""} onChange={(e) => onChange(e.target.value)} />;
    case "BOOLEAN": {
      const sel = value === true ? "true" : value === false ? "false" : "";
      return (
        <div className="fawkes-wizards__keuzes">
          {[
            ["true", t("wizards.ja")],
            ["false", t("wizards.nee")],
          ].map(([v, l]) => (
            <button
              type="button"
              key={v}
              className={`fawkes-wizards__keuze${sel === v ? " fawkes-wizards__keuze--gekozen" : ""}`}
              onClick={() => onChange(v === "true")}
            >
              <span className="fawkes-wizards__keuzelabel">{l}</span>
            </button>
          ))}
        </div>
      );
    }
    case "DATE":
      return <DateField className="fui-field__input" value={typeof value === "string" ? value : ""} onChange={(e) => onChange(e.target.value)} />;
    case "SELECT":
      return <KeuzeKnoppen f={f} value={value} onChange={onChange} multi={false} />;
    case "MULTISELECT":
      return <KeuzeKnoppen f={f} value={value} onChange={onChange} multi={true} />;
    default:
      return <input className="fui-field__input" value={typeof value === "string" ? value : ""} onChange={(e) => onChange(e.target.value)} />;
  }
}

// ── one wizard card (hybrid WizardKaart) ────────────────────────────────────────────────────────────
function WizardKaart({ w, locale, onDetail, t }: { w: WizardCard; locale: string; onDetail: (runId: string) => void; t: (k: string) => string }) {
  const lr = w.laatsteRun;
  const velden = lr?.samenvatting ?? [];
  return (
    <div className="fawkes-wizards__kaart">
      <div className="fawkes-wizards__kaartkop">
        <span className="fawkes-wizards__kaarticon">{w.icon || "🧩"}</span>
        <div className="fawkes-wizards__kaartnaam">
          <strong>{w.tegelTitel}</strong>
          {lr?.status ? (
            <StatusChip status={lr.status} mini />
          ) : (
            w.category && <span className="fawkes-wizards__chip fawkes-wizards__chip--mini fawkes-wizards__chip--grijs">{w.category}</span>
          )}
        </div>
        <div className="fawkes-wizards__kaartvdiv" />
        <div className="fawkes-wizards__kaartmeta">
          <span className="fawkes-wizards__metaicon">🗓️</span>
          {lr ? (
            <div className="fawkes-wizards__metatekst">
              <span className="fawkes-muted fawkes-wizards__klein">{t("wizards.laatstBijgewerkt")}</span>
              <span>
                <strong>{formatDateTime(lr.completedAt, locale)}</strong>
                {lr.completedBy ? ` · ${lr.completedBy}` : ""}
              </span>
            </div>
          ) : (
            <span className="fawkes-muted fawkes-wizards__klein">{t("wizards.nogGeenRegistratie")}</span>
          )}
        </div>
      </div>
      {velden.length > 0 && (
        <>
          <div className="fawkes-wizards__kaarthr" />
          <div className="fawkes-wizards__velden">
            {velden.map((s, i) => (
              <div className="fawkes-wizards__veld" key={i}>
                <span className="fawkes-wizards__veldicon">{iconVoor(s.fieldType)}</span>
                <div className="fawkes-wizards__veldtekst">
                  <span className="fawkes-muted fawkes-wizards__klein">{s.label}</span>
                  <strong>{s.waarde}</strong>
                </div>
              </div>
            ))}
          </div>
          {lr && (
            <button type="button" className="fawkes-wizards__meer" onClick={() => onDetail(lr.id)}>
              {t("wizards.meerDetails")} ⌄
            </button>
          )}
        </>
      )}
    </div>
  );
}

// The runner-sidebar photo circle (Jmix dierFoto): authenticated blob fetch → object URL, silently
// blank when the animal has no photo (the circle stays as a muted disc, like the hybrid).
function DierFoto({ animalId }: { animalId: string }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let revoked: string | null = null;
    let cancelled = false;
    fetchAnimalPhotoBlob(animalId)
      .then((blob) => {
        if (cancelled) return;
        revoked = URL.createObjectURL(blob);
        setUrl(revoked);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [animalId]);
  return <div className="fawkes-wizards__dierfoto" style={url ? { backgroundImage: `url(${url})` } : undefined} />;
}

// ── runner (hybrid Runner + DierZijbalk) ────────────────────────────────────────────────────────────
function Runner({
  form,
  dier,
  animalId,
  saving,
  failed,
  onSubmit,
  onCancel,
  t,
}: {
  form: WizardRunForm;
  dier: WizardsTilePayload["dier"];
  animalId: string;
  saving: boolean;
  failed: boolean;
  onSubmit: (waarden: WaardenState, draft: boolean) => void;
  onCancel: () => void;
  t: (k: string) => string;
}) {
  const steps = form.snapshot.steps ?? [];
  const [stap, setStap] = useState(0);
  const [waarden, setWaarden] = useState<WaardenState>({});
  const [fout, setFout] = useState<string | null>(null);
  const huidige = steps[stap] ?? { title: "", fields: [] };

  const setVeld = (key: string, v: unknown) => setWaarden((cur) => ({ ...cur, [key]: v }));
  const valideer = (velden: WizardFormField[]): boolean => {
    for (const f of velden) {
      if (!f.required) continue;
      const v = waarden[f.fieldKey];
      const leeg = v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);
      if (f.fieldType !== "BOOLEAN" && leeg) {
        setFout(t("wizards.verplicht").replace("%s", f.label));
        return false;
      }
    }
    setFout(null);
    return true;
  };
  const volgende = () => {
    if (!valideer(huidige.fields ?? [])) return;
    setStap((s) => Math.min(s + 1, steps.length - 1));
  };
  const vorige = () => {
    setFout(null);
    setStap((s) => Math.max(s - 1, 0));
  };
  const opslaan = () => {
    if (!valideer(huidige.fields ?? [])) return;
    onSubmit(waarden, false);
  };

  const laatste = stap === steps.length - 1;
  return (
    <div className="fawkes-wizards__runner">
      {form.snapshot.description && (
        <p className="fawkes-muted fawkes-wizards__klein fawkes-wizards__runneroms">{form.snapshot.description}</p>
      )}
      <ol className="fawkes-wizards__stapper">
        {steps.map((s, i) => (
          <li
            key={i}
            className={`fawkes-wizards__stapperitem${i === stap ? " fawkes-wizards__stapperitem--actief" : i < stap ? " fawkes-wizards__stapperitem--klaar" : ""}`}
            onClick={() => {
              if (i < stap) {
                setFout(null);
                setStap(i);
              }
            }}
          >
            <span className="fawkes-wizards__stappernr">{i < stap ? "✓" : i + 1}</span>
            <span className="fawkes-wizards__stappertekst">
              <strong>{s.title}</strong>
              {s.description && <span className="fawkes-muted fawkes-wizards__klein">{s.description}</span>}
            </span>
          </li>
        ))}
      </ol>

      <div className="fawkes-wizards__runnerlayout">
        <div className="fawkes-wizards__runnermain">
          {(huidige.fields ?? []).map((f) => (
            <div className="fawkes-wizards__runnerveld" key={f.fieldKey}>
              <span className="fawkes-wizards__veldlabel">
                {f.label}
                {f.required && <span className="fawkes-wizards__ster">*</span>}
              </span>
              <VeldInput f={f} value={waarden[f.fieldKey]} onChange={(v) => setVeld(f.fieldKey, v)} t={t} />
              {f.helpText && <div className="fawkes-muted fawkes-wizards__klein">{f.helpText}</div>}
            </div>
          ))}
          {(huidige.fields ?? []).length === 0 && <p className="fawkes-muted">{t("wizards.geenVelden")}</p>}
          {fout && <p className="fawkes-error">{fout}</p>}
          {failed && <p className="fawkes-error">{t("wizards.fout")}</p>}
        </div>

        {dier && (
          <aside className="fawkes-wizards__zijbalk">
            <div className="fawkes-wizards__dierkaart">
              <DierFoto animalId={animalId} />
              <strong>{dier.naam ?? ""}</strong>
              <span className="fawkes-muted fawkes-wizards__klein">{dier.ras ?? dier.diersoort ?? ""}</span>
              {dier.verblijf?.dagen != null && (
                <span className="fawkes-wizards__dierbadge">{t("wizards.inAsielSinds").replace("%d", String(dier.verblijf.dagen))}</span>
              )}
            </div>
            <dl className="fawkes-wizards__dierinfo">
              {dier.chipnummer && (
                <>
                  <dt>{t("wizards.chipnummer")}</dt>
                  <dd>{dier.chipnummer}</dd>
                </>
              )}
              {dier.geboortedatum && (
                <>
                  <dt>{t("wizards.geboortedatum")}</dt>
                  <dd>{dier.geboortedatum}</dd>
                </>
              )}
              {dier.geslacht && (
                <>
                  <dt>{t("wizards.geslacht")}</dt>
                  <dd>{dier.geslacht}</dd>
                </>
              )}
              {dier.leeftijd && (
                <>
                  <dt>{t("wizards.leeftijd")}</dt>
                  <dd>{dier.leeftijd}</dd>
                </>
              )}
            </dl>
            {dier.verblijf && (
              <div className="fawkes-wizards__verblijfblok">
                <div className="fawkes-wizards__blokkop">
                  {t("wizards.verblijfstatus")} {dier.verblijf.actief && <span className="fawkes-wizards__actief">{t("wizards.actief")}</span>}
                </div>
                {dier.verblijf.reden && <div className="fawkes-wizards__verblijfreden">{dier.verblijf.reden}</div>}
                <div className="fawkes-muted fawkes-wizards__klein">
                  {dier.verblijf.nummer ? `${dier.verblijf.nummer} · ` : ""}
                  {dier.verblijf.sinds ? `${t("wizards.sinds")} ${dier.verblijf.sinds}` : ""}
                  {dier.verblijf.dagen != null ? ` · ${dier.verblijf.dagen} ${t("wizards.dagen")}` : ""}
                </div>
              </div>
            )}
            <div className="fawkes-wizards__samenvblok">
              <div className="fawkes-wizards__blokkop">{t("wizards.samenvatting")}</div>
              <ol className="fawkes-wizards__samenvlijst">
                {steps.map((s, i) => (
                  <li key={i} className={i === stap ? "fawkes-wizards__samenv--nu" : i < stap ? "fawkes-wizards__samenv--klaar" : ""}>
                    <span className="fawkes-wizards__samenvnr">{i < stap ? "✓" : i + 1}</span>
                    <span className="fawkes-wizards__samenvtekst">
                      <strong>{s.title}</strong>
                      <span className="fawkes-muted fawkes-wizards__klein">
                        {i < stap ? t("wizards.stap.ingevuld") : i === stap ? t("wizards.stap.bezig") : t("wizards.stap.wacht")}
                      </span>
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          </aside>
        )}
      </div>

      <div className="fawkes-wizards__runnervoet">
        <Button variant="secondary" disabled={stap === 0 || saving} onClick={vorige}>
          ‹ {t("wizards.vorige")}
        </Button>
        <Button variant="secondary" disabled={saving} onClick={() => onSubmit(waarden, true)}>
          📄 {t("wizards.concept")}
        </Button>
        <span className="fawkes-wizards__spacer" />
        <Button variant="secondary" disabled={saving} onClick={onCancel}>
          {t("common.cancel")}
        </Button>
        {laatste ? (
          <Button disabled={saving} onClick={opslaan}>
            {t("wizards.afronden")} ✓
          </Button>
        ) : (
          <Button disabled={saving} onClick={volgende}>
            {t("wizards.volgende")} ›
          </Button>
        )}
      </div>
    </div>
  );
}

// ── the animal-detail tile ──────────────────────────────────────────────────────────────────────────
export function WizardsTile({ animalId }: { animalId: string }) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";

  const { data, isLoading, isError, refetch } = useCustom({ url: `animals/${animalId}/wizards`, method: "get" });
  const payload = data?.data as WizardsTilePayload | undefined;

  const { mutate, isLoading: saving } = useCustomMutation();
  const [failed, setFailed] = useState(false);
  const [kiezerOpen, setKiezerOpen] = useState(false);
  const [historieOpen, setHistorieOpen] = useState(false);
  const [form, setForm] = useState<WizardRunForm | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [detail, setDetail] = useState<WizardRunDetail | null>(null);

  // The form + detail are fetched on demand (the hybrid asked the host per postMessage); useCustom
  // is declarative, so these one-shot loads go through the mutation-less fetch of the dataProvider
  // via a manual refetch pattern: a plain GET with useCustomMutation is not possible, so we lazily
  // fetch with the browser fetch through the same relative /api base the dataProvider uses.
  const start = async (definitionId: string) => {
    setFailed(false);
    setFormLoading(true);
    try {
      setForm(await getJson<WizardRunForm>(`animals/${animalId}/wizards/form/${definitionId}`));
      setKiezerOpen(false);
    } catch {
      setFailed(true);
    } finally {
      setFormLoading(false);
    }
  };

  // `historieOpen` is left as-is when the detail opens from the history list, so closing the
  // detail falls back to the view it came from (the hybrid's detailVan bookkeeping).
  const openDetail = async (runId: string, vanHistorie: boolean) => {
    setFailed(false);
    try {
      setDetail(await getJson<WizardRunDetail>(`animals/${animalId}/wizards/runs/${runId}`));
      if (!vanHistorie) setHistorieOpen(false);
    } catch {
      setFailed(true);
    }
  };

  const submit = (waarden: WaardenState, draft: boolean) => {
    if (!form) return;
    setFailed(false);
    mutate(
      {
        url: `animals/${animalId}/wizards/runs`,
        method: "post",
        values: { definitionId: form.definitionId, values: waarden, draft },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          setForm(null);
          refetch();
        },
        onError: () => setFailed(true),
      },
    );
  };

  if (isLoading) return <p className="fawkes-muted">{t("common.loading")}</p>;
  if (isError || !payload) return <p className="fawkes-error">{t("animalDetailView.tileLoadFailed")}</p>;

  const wizards = payload.wizards ?? [];
  const runs = payload.runs ?? [];

  // Jmix parity (verified against the running :8080 tile): the kiezer/historie/detail replace the
  // tile content INLINE (the hybrid's modus switch); only the runner pops as a modal overlay.
  const closeDetail = () => setDetail(null);

  let body: ReactNode;
  if (detail) {
    body = (
      <div className="fawkes-wizards__inline">
        <div className="fawkes-wizards__inlinekop">
          <div>
            <strong>{detail.snapshot?.name ?? t("wizards.detail.titel")}</strong>
            <div className="fawkes-muted fawkes-wizards__klein">
              {formatDateTime(detail.completedAt, locale)}
              {detail.completedBy ? ` · ${detail.completedBy}` : ""}
              {detail.versionNumber ? ` · v${detail.versionNumber}` : ""}
            </div>
          </div>
          <button type="button" className="fawkes-wizards__sluit" aria-label={t("common.close")} onClick={closeDetail}>
            ✕
          </button>
        </div>
        <StatusChip status={detail.status} />
        <dl className="fawkes-wizards__detaillijst">
          {detail.values.map((v, i) => (
            <div className="fawkes-wizards__detailrij" key={i}>
              <dt>{v.label}</dt>
              <dd>{v.weergave}</dd>
            </div>
          ))}
          {detail.values.length === 0 && <dd className="fawkes-muted">{t("wizards.detail.leeg")}</dd>}
        </dl>
      </div>
    );
  } else if (historieOpen) {
    body = (
      <div className="fawkes-wizards__inline">
        <div className="fawkes-wizards__inlinekop">
          <strong>{t("wizards.historie")}</strong>
          <button type="button" className="fawkes-wizards__sluit" aria-label={t("common.close")} onClick={() => setHistorieOpen(false)}>
            ✕
          </button>
        </div>
        {runs.length === 0 ? (
          <p className="fawkes-muted">{t("wizards.historie.leeg")}</p>
        ) : (
          <ul className="fawkes-wizards__historie">
            {runs.map((r) => (
              <li key={r.id} onClick={() => void openDetail(r.id, true)}>
                <div className="fawkes-wizards__hrij">
                  <strong>{r.naam}</strong>
                  <span className="fawkes-wizards__hrechts">
                    <StatusChip status={r.status} mini />
                    <span className="fawkes-muted fawkes-wizards__klein">{formatDateTime(r.completedAt, locale)}</span>
                  </span>
                </div>
                <div className="fawkes-muted fawkes-wizards__klein">
                  {(r.samenvatting ?? []).map((s) => `${s.label}: ${s.waarde}`).join(" · ")}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  } else if (kiezerOpen) {
    body = (
      <div className="fawkes-wizards__inline">
        <div className="fawkes-wizards__inlinekop">
          <strong>{t("wizards.kiezer.titel")}</strong>
          <button type="button" className="fawkes-wizards__sluit" aria-label={t("common.close")} onClick={() => setKiezerOpen(false)}>
            ✕
          </button>
        </div>
        <div className="fawkes-wizards__kiezerlijst">
          {wizards.length === 0 && <p className="fawkes-muted">{t("wizards.leeg")}</p>}
          {wizards.map((w) => (
            <button type="button" key={w.definitionId} className="fawkes-wizards__kiezeritem" disabled={formLoading} onClick={() => void start(w.definitionId)}>
              <span className="fawkes-wizards__kiezericon">{w.icon || "🧩"}</span>
              <span className="fawkes-wizards__kiezertekst">
                <strong>{w.tegelTitel}</strong>
                {w.category && <span className="fawkes-muted fawkes-wizards__klein">{w.category}</span>}
              </span>
              <span className="fawkes-wizards__kiezerpijl">›</span>
            </button>
          ))}
        </div>
      </div>
    );
  } else {
    body = (
      <>
        <div className="fawkes-wizards__kop">
          <span className="fawkes-wizards__badge">
            {wizards.length} {wizards.length === 1 ? t("wizards.badge.enkel") : t("wizards.badge.meer")}
          </span>
          <span className="fawkes-wizards__spacer" />
          {runs.length > 0 && (
            <Button variant="secondary" onClick={() => setHistorieOpen(true)}>
              🕘 {t("wizards.historie")} ({runs.length})
            </Button>
          )}
          {wizards.length > 0 && payload.magUitvoeren && (
            <Button onClick={() => setKiezerOpen(true)}>
              <PlusCircleSolidIcon width={18} height={18} /> {t("wizards.nieuw")}
            </Button>
          )}
        </div>

        {wizards.length === 0 ? (
          <p className="fawkes-tile-placeholder">{t("wizards.leeg")}</p>
        ) : (
          <div className="fawkes-wizards__kaarten">
            {wizards.map((w) => (
              <WizardKaart key={w.definitionId} w={w} locale={locale} onDetail={(runId) => void openDetail(runId, false)} t={t} />
            ))}
          </div>
        )}
      </>
    );
  }

  return (
    <div className="fawkes-wizards">
      {failed && !form && <p className="fawkes-error">{t("wizards.fout")}</p>}
      {body}

      {/* Runner (hybrid Runner, widened via .fui-modal:has(.fawkes-wizards__runner)). */}
      {form && (
        <Modal open title={t("wizards.runner.titel").replace("%s", form.name)} onClose={() => setForm(null)}>
          <Runner form={form} dier={payload.dier ?? null} animalId={animalId} saving={saving} failed={failed} onSubmit={submit} onCancel={() => setForm(null)} t={t} />
        </Modal>
      )}
    </div>
  );
}

/**
 * Eén wizard uitvoeren, los van de wizardtegel: haalt het formulier op en zet de runner in een dialoog.
 *
 * <p>Bestaat sinds een taakregel een wizard kan starten (2026-08-21). Zo'n taak staat in de takentegel,
 * en daar moet je hem ook kunnen afwerken — de gebruiker dwingen om naar de wizardtegel te lopen en de
 * juiste wizard uit de lijst te zoeken is precies de omweg die de taak zou moeten wegnemen.
 *
 * <p>{@code onKlaar} krijgt te horen of het een concept was: alleen een afgeronde wizard mag de taak
 * afsluiten, want een half ingevuld formulier is geen gedane zaak.
 */
export function WizardRunModal({
  animalId,
  definitionId,
  onKlaar,
  onClose,
}: {
  animalId: string;
  definitionId: string;
  onKlaar: (concept: boolean) => void;
  onClose: () => void;
}) {
  const t = useTranslate();
  const { mutate, isLoading: saving } = useCustomMutation();
  const [form, setForm] = useState<WizardRunForm | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let levend = true;
    getJson<WizardRunForm>(`animals/${animalId}/wizards/form/${definitionId}`)
      .then((f) => { if (levend) setForm(f); })
      .catch(() => { if (levend) setFailed(true); });
    return () => { levend = false; };
  }, [animalId, definitionId]);

  const submit = (waarden: WaardenState, draft: boolean) => {
    if (!form) return;
    setFailed(false);
    mutate(
      {
        url: `animals/${animalId}/wizards/runs`,
        method: "post",
        values: { definitionId: form.definitionId, values: waarden, draft },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => onKlaar(draft),
        onError: () => setFailed(true),
      },
    );
  };

  if (!form) {
    return (
      <Modal open title={t("wizards.kiezer.titel")} onClose={onClose}>
        {failed ? <p className="fawkes-error">{t("wizards.fout")}</p> : <p className="fawkes-muted">{t("common.loading")}</p>}
      </Modal>
    );
  }

  return (
    <Modal open title={t("wizards.runner.titel").replace("%s", form.name)} onClose={onClose}>
      <Runner
        form={form}
        dier={null}
        animalId={animalId}
        saving={saving}
        failed={failed}
        onSubmit={submit}
        onCancel={onClose}
        t={t}
      />
    </Modal>
  );
}

// One-shot authenticated GET for the on-demand form/detail loads (the hybrid asked its host per
// postMessage; useCustom is declarative). Bearer + base mirror the media.ts helper: the access token
// lives in memory, so a plain fetch must attach it itself.
async function getJson<T>(path: string): Promise<T> {
  const headers: Record<string, string> = {};
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API_URL}/${path}`, { headers });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as T;
}
